import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Emergency beep sound - continuous alarm
const BEEP_FREQUENCY = 800;
const BEEP_DURATION = 200;
const BEEP_INTERVAL = 400;

export default function RedCodeAlert() {
  const { token, user } = useAuth();
  const [activeAlert, setActiveAlert] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const audioContextRef = useRef(null);
  const beepIntervalRef = useRef(null);

  // Check for active alerts periodically
  useEffect(() => {
    if (!token || !user?.store) return;

    const checkAlert = async () => {
      try {
        const res = await fetch(`${API_URL}/api/red-code/active`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.active && data.alert) {
            setActiveAlert(data.alert);
          } else {
            setActiveAlert(null);
          }
        }
      } catch (err) {
        console.error('Error checking red code:', err);
      }
    };

    // Check immediately
    checkAlert();

    // Check every 3 seconds
    const interval = setInterval(checkAlert, 3000);

    return () => clearInterval(interval);
  }, [token, user?.store]);

  // Start/stop beeping based on active alert
  useEffect(() => {
    if (activeAlert) {
      startBeeping();
    } else {
      stopBeeping();
    }

    return () => stopBeeping();
  }, [activeAlert]);

  const startBeeping = () => {
    if (beepIntervalRef.current) return;

    try {
      // Create audio context
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      
      const playBeep = () => {
        if (!audioContextRef.current) return;
        
        const oscillator = audioContextRef.current.createOscillator();
        const gainNode = audioContextRef.current.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContextRef.current.destination);
        
        oscillator.frequency.value = BEEP_FREQUENCY;
        oscillator.type = 'square';
        
        gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
        
        oscillator.start(audioContextRef.current.currentTime);
        oscillator.stop(audioContextRef.current.currentTime + BEEP_DURATION / 1000);
      };

      // Play immediately
      playBeep();

      // Continue beeping
      beepIntervalRef.current = setInterval(playBeep, BEEP_INTERVAL);
    } catch (err) {
      console.error('Error starting beep:', err);
    }
  };

  const stopBeeping = () => {
    if (beepIntervalRef.current) {
      clearInterval(beepIntervalRef.current);
      beepIntervalRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };

  const handleCancel = async () => {
    if (!activeAlert) return;
    
    setCanceling(true);
    try {
      const res = await fetch(`${API_URL}/api/red-code/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ alert_id: activeAlert.id })
      });

      if (res.ok) {
        toast.success('Red Code Alert Canceled - All Clear');
        setActiveAlert(null);
        stopBeeping();
      } else {
        toast.error('Failed to cancel alert');
      }
    } catch (err) {
      toast.error('Error canceling alert');
    } finally {
      setCanceling(false);
    }
  };

  if (!activeAlert) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Flashing red overlay */}
      <div className="absolute inset-0 bg-red-600 animate-pulse opacity-90" />
      
      {/* Alert content */}
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center animate-bounce">
        <div className="w-24 h-24 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
          <AlertTriangle className="w-14 h-14 text-white" />
        </div>
        
        <h1 className="text-4xl font-black text-red-600 mb-2">
          🚨 RED CODE 🚨
        </h1>
        
        <h2 className="text-2xl font-bold text-slate-800 mb-4">
          EMERGENCY ALERT
        </h2>
        
        <div className="bg-red-100 rounded-xl p-4 mb-6">
          <p className="text-xl font-bold text-red-800 mb-2">
            GATHER AT COUNTER
          </p>
          <p className="text-lg text-red-700">
            {activeAlert.store}
          </p>
        </div>
        
        <div className="text-sm text-slate-500 mb-6">
          <p>Triggered by: <span className="font-semibold">{activeAlert.triggered_by}</span></p>
          <p>At: {new Date(activeAlert.triggered_at).toLocaleTimeString()}</p>
        </div>
        
        <Button
          onClick={handleCancel}
          disabled={canceling}
          className="w-full bg-green-600 hover:bg-green-700 text-white text-xl py-6 rounded-xl font-bold"
        >
          {canceling ? 'Canceling...' : '✓ ALL CLEAR - Cancel Alert'}
        </Button>
        
        <p className="text-xs text-slate-400 mt-4">
          Press above to stop the alarm when emergency is resolved
        </p>
      </div>
    </div>
  );
}
